/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import template from './reference-data-page.component.html';

import {AppSharedModule} from '../../common/app-shared.module';
import {AdminListModule} from '../../common/directives/admin-list.module';
import {ReferenceDataUploadComponent} from './reference-data-upload.component';
import {TargetingSearchApiService} from '../../targeting/targeting-search-api.service';

interface ReferenceDataSearchMeta {
  pageSize: number;
  currentPage: number;
  resolved: boolean;
  changePage: () => void;
}

interface ReferenceDataSearchState {
  term: string;
}

@Component({
  standalone: true,
  selector: 'adn-reference-data-page',
  imports: [AppSharedModule, FormsModule, TranslateModule, AdminListModule, ReferenceDataUploadComponent],
  template
})
export class ReferenceDataPageComponent implements OnInit, OnDestroy {
  search: ReferenceDataSearchState = {term: ''};
  kvSearch: ReferenceDataSearchState = {term: ''};
  list: any = {searchResults: []};
  kvList: any = {searchResults: []};

  searchMeta: ReferenceDataSearchMeta = {
    pageSize: 50,
    currentPage: 1,
    resolved: true,
    changePage: () => this.searchCategories()
  };
  kvSearchMeta: ReferenceDataSearchMeta = {
    pageSize: 50,
    currentPage: 1,
    resolved: true,
    changePage: () => this.searchKeyValues()
  };

  private searchHookTimer: ReturnType<typeof setTimeout> | null = null;
  private kvSearchHookTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly targetingSearchApi: TargetingSearchApiService,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.searchCategories();
    this.searchKeyValues();
  }

  ngOnDestroy(): void {
    if (this.searchHookTimer) {
      clearTimeout(this.searchHookTimer);
    }
    if (this.kvSearchHookTimer) {
      clearTimeout(this.kvSearchHookTimer);
    }
  }

  searchCategories(newSearch = false): void {
    this.searchMeta.resolved = false;

    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }

    this.targetingSearchApi.searchCategories({
      q: this.search.term || '',
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage - 1,
      origin: 'UPLOAD'
    }).subscribe((page) => {
      this.ngZone.run(() => {
        this.list = page;
        this.searchMeta.resolved = true;
      });
    });
  }

  searchKeyValues(newSearch = false): void {
    this.kvSearchMeta.resolved = false;

    if (newSearch) {
      this.kvSearchMeta.currentPage = 1;
    }

    this.targetingSearchApi.searchKeyValues({
      q: this.kvSearch.term || '',
      pageSize: this.kvSearchMeta.pageSize,
      page: this.kvSearchMeta.currentPage - 1
    }).subscribe((page) => {
      this.ngZone.run(() => {
        this.kvList = page;
        this.kvSearchMeta.resolved = true;
      });
    });
  }

  searchHook(): void {
    if (this.searchHookTimer) {
      clearTimeout(this.searchHookTimer);
    }
    this.searchHookTimer = setTimeout(() => {
      this.ngZone.run(() => this.searchCategories(true));
    }, 1000);
  }

  kvSearchHook(): void {
    if (this.kvSearchHookTimer) {
      clearTimeout(this.kvSearchHookTimer);
    }
    this.kvSearchHookTimer = setTimeout(() => {
      this.ngZone.run(() => this.searchKeyValues(true));
    }, 500);
  }
}
